
import { useState } from 'react';
import { useWordPressComments } from '@/hooks/useWordPress';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ChatBubbleLeftEllipsisIcon, UserIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/hooks/use-toast';
import { sanitizeHtml, sanitizeTextContent } from '@/utils/sanitizeHtml';

interface CommentsProps {
  postId: number;
}

interface CommentFormData {
  author_name: string;
  author_email: string;
  content: string;
}

const Comments = ({ postId }: CommentsProps) => {
  const { data: comments, isLoading, error } = useWordPressComments(postId);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CommentFormData>({
    author_name: '',
    author_email: '',
    content: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.author_name || !formData.author_email || !formData.content) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    // Basic input validation and sanitization
    const sanitizedData = {
      author_name: sanitizeTextContent(formData.author_name.trim()),
      author_email: formData.author_email.trim().toLowerCase(),
      content: sanitizeTextContent(formData.content.trim())
    };

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitizedData.author_email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    // Content length validation
    if (sanitizedData.content.length < 10) {
      toast({
        title: 'Comment Too Short',
        description: 'Please write a comment with at least 10 characters.',
        variant: 'destructive',
      });
      return;
    }

    if (sanitizedData.content.length > 1000) {
      toast({
        title: 'Comment Too Long',
        description: 'Please keep your comment under 1000 characters.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // In a real implementation, you would submit to WordPress comments API
      // For now, we'll show a success message
      toast({
        title: 'Comment Submitted',
        description: 'Your comment has been submitted for moderation.',
      });
      
      setFormData({ author_name: '', author_email: '', content: '' });
      setShowForm(false);
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: 'Failed to submit comment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mt-12 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-12">
        <h3 className="text-xl font-semibold text-foreground mb-4">Comments</h3>
        <p className="text-muted-foreground">Unable to load comments at this time.</p>
      </div>
    );
  }

  const commentCount = comments?.length || 0;

  return (
    <div className="mt-12 border-t pt-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <ChatBubbleLeftEllipsisIcon className="w-5 h-5" />
          Comments ({commentCount})
        </h3>
        <Button 
          variant="outline" 
          onClick={() => setShowForm(!showForm)}
          size="sm"
        >
          {showForm ? 'Cancel' : 'Leave a Comment'}
        </Button>
      </div>

      {/* Comment Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-muted p-6 rounded-lg mb-8 space-y-4">
          <h4 className="font-semibold text-foreground">Leave a Comment</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="Your Name *"
              value={formData.author_name}
              onChange={(e) => setFormData(prev => ({ ...prev, author_name: e.target.value }))}
              maxLength={100}
              required
            />
            <Input
              type="email"
              placeholder="Your Email *"
              value={formData.author_email}
              onChange={(e) => setFormData(prev => ({ ...prev, author_email: e.target.value }))}
              maxLength={254}
              required
            />
          </div>
          <Textarea
            placeholder="Write your comment... *"
            value={formData.content}
            onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
            className="min-h-24"
            maxLength={1000}
            required
          />
          <div className="text-sm text-muted-foreground">
            {formData.content.length}/1000 characters
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Comment'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Comments List */}
      {commentCount === 0 ? (
        <div className="text-center py-8">
          <ChatBubbleLeftEllipsisIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No comments yet. Be the first to share your thoughts!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {comments?.map((comment) => (
            <div key={comment.id} className="border-l-2 border-muted pl-4 py-2">
              <div className="flex items-center gap-2 mb-2">
                <UserIcon className="w-4 h-4 text-muted-foreground" />
                <span className="font-semibold text-foreground">
                  {sanitizeTextContent(comment.author_name)}
                </span>
                <CalendarIcon className="w-4 h-4 text-muted-foreground ml-2" />
                <span className="text-sm text-muted-foreground">
                  {new Date(comment.date).toLocaleDateString()}
                </span>
              </div>
              <div 
                className="prose prose-sm max-w-none text-muted-foreground"
                dangerouslySetInnerHTML={{ 
                  __html: sanitizeHtml(comment.content.rendered, {
                    allowLinks: true,
                    allowBasicFormatting: true,
                    allowImages: false
                  })
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Comments;
